import { ShowContextProvider, ShowView, TabbedShowLayout, Tab, TextField, DateField, useShowController, FunctionField, ReferenceManyField, Datagrid, Pagination } from 'react-admin';
import { Chip, Typography, Box, FormControl, InputLabel, Select, MenuItem, Collapse } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useState } from 'react';
import JsonView from '@uiw/react-json-view';
import ContextTitle from '../../Components/ContextTitle';
import { useJsonViewStyle } from '../../Components/Layout/jsonViewTheme';
import { Webhook, WebhookEvent } from '../../types/webhooks';

const StatusChip = ({ record }: { record: Webhook }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  return <Chip label={record.status} color={getStatusColor(record.status)} size="small" variant="outlined" />;
};

const EventStatusChip = ({ record }: { record: WebhookEvent }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  return <Chip label={record.status} color={getStatusColor(record.status)} size="small" variant="outlined" />;
};

const ExpandableJsonField = ({ record }: { record: Record<string, any> }) => {
  const [expanded, setExpanded] = useState(false);
  const jsonStyle = useJsonViewStyle({ fontSize: '0.75rem' });
  const payload = record?.event?.payload;

  if (!payload) {
    return null;
  }

  return (
    <Box sx={{ width: '400px', maxWidth: '400px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        {expanded ? <ExpandLess /> : <ExpandMore />}
        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
          {expanded ? 'Hide JSON' : 'Show JSON'}
        </Typography>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ mt: 1, maxHeight: '500px', overflow: 'auto', width: '400px' }}>
          <JsonView value={payload} collapsed={false} displayDataTypes={false} displayObjectSize={false} style={jsonStyle} />
        </Box>
      </Collapse>
    </Box>
  );
};

const WebhookShow = () => {
  const controllerProps = useShowController();
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value);
  };

  return (
    <ShowContextProvider value={controllerProps}>
      <ShowView title={<ContextTitle context="url" />}>
        <TabbedShowLayout>
          <Tab label="General">
            <TextField source="uuid" label="UUID" />
            <TextField source="webhookUrl" label="URL" />
            <FunctionField label="Events" render={(record?: Record<string, any>) => (record?.eventTypes && Array.isArray(record.eventTypes) ? record.eventTypes.join(', ') : null)} />
            <FunctionField label="Status" render={(record?: Record<string, any>) => (record ? <StatusChip record={record as Webhook} /> : null)} />
            <TextField source="companyName" label="Company Name" />
            <TextField source="companyUuid" label="Company UUID" />
            <DateField source="createdAt" label="Created At" showTime />
            <DateField source="updatedAt" label="Updated At" showTime />
          </Tab>
          <Tab label="Events">
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6">Webhook Events</Typography>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="status-filter-label">Filter by Status</InputLabel>
                <Select labelId="status-filter-label" id="status-filter" value={statusFilter} label="Filter by Status" onChange={handleStatusFilterChange}>
                  <MenuItem value="all">All Events</MenuItem>
                  <MenuItem value="completed">
                    <Chip label="Completed" color="success" size="small" variant="outlined" />
                  </MenuItem>
                  <MenuItem value="pending">
                    <Chip label="Pending" color="warning" size="small" variant="outlined" />
                  </MenuItem>
                  <MenuItem value="failed">
                    <Chip label="Failed" color="error" size="small" variant="outlined" />
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
            <ReferenceManyField
              reference={`webhooks/${controllerProps.record?.id}/events`}
              target="webhook"
              filter={statusFilter !== 'all' ? { status: statusFilter } : {}}
              perPage={25}
              sort={{ field: 'createdAt', order: 'DESC' }}
              pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
            >
              <Datagrid bulkActionButtons={false}>
                <TextField source="uuid" label="Event UUID" cellClassName="uuid-column" />
                <FunctionField label="Status" render={(record?: Record<string, any>) => (record ? <EventStatusChip record={record as WebhookEvent} /> : null)} />
                <TextField source="event.type" label="Event Type" />
                <FunctionField label="Payload" cellClassName="payload-column" render={(record?: Record<string, any>) => (record ? <ExpandableJsonField record={record} /> : null)} />
                <FunctionField label="Error Message" render={(record?: WebhookEvent) => record?.error?.err?.response?._text || record?.error?.message || '-'} />
                <TextField source="retryNumber" label="Retry #" />
                <DateField source="createdAt" label="Created At" showTime />
                <DateField source="lastSendAt" label="Last Sent" showTime />
              </Datagrid>
            </ReferenceManyField>
          </Tab>
        </TabbedShowLayout>
      </ShowView>
    </ShowContextProvider>
  );
};

export default WebhookShow;
